/**
 * material_fx — converting a cooked GLTF material to a PHYSICAL one so the
 * material-FX layers (clearcoat on metals, iridescence on teal emissives) can
 * be applied.
 *
 * WHY THIS IS NOT JUST `new MeshPhysicalMaterial().copy(mat)` (2026-09-14).
 * The classic `MeshPhysicalMaterial.copy()` reads physical-only properties off
 * the SOURCE without guarding them:
 *
 *   this.clearcoatNormalScale.copy( source.clearcoatNormalScale );
 *   this.iridescenceThicknessRange = [ ...source.iridescenceThicknessRange ];
 *
 * (`node_modules/three/src/materials/MeshPhysicalMaterial.js:538,546`). A
 * `MeshStandardMaterial` — what GLTFLoader hands out, and what every cooked
 * asset therefore carries until three converts it at first render — has
 * neither, so both lines throw. The conversion sat inside a `try`, so the
 * throw was swallowed: no clearcoat, no iridescence, AND the snow accumulation
 * layer that shared the block was skipped for that material too.
 *
 * The NODE path does not have this problem — `NodeMaterial.copy()` guards
 * every read with `source[property] !== undefined` — which is why this only
 * ever bit classic materials.
 */
import * as THREE from 'three/webgpu';

/**
 * Build a `MeshPhysical*Material` carrying `src`'s standard properties.
 *
 * `isNode` selects the node variant (and its own defensive copy); the classic
 * variant is copied through `MeshStandardMaterial.prototype.copy`, which reads
 * exactly the fields a standard material actually has. The physical-only
 * properties keep their constructor defaults, which is correct: the source has
 * no values for them.
 */
export function toPhysicalMaterial(
  src: THREE.MeshStandardMaterial,
  isNode: boolean,
): THREE.MeshPhysicalMaterial {
  if (isNode) {
    const phys = new THREE.MeshPhysicalNodeMaterial();
    (phys as unknown as { copy(s: unknown): void }).copy(src);
    return phys as unknown as THREE.MeshPhysicalMaterial;
  }
  const phys = new THREE.MeshPhysicalMaterial();
  // NOT phys.copy(src) — see the file header.
  THREE.MeshStandardMaterial.prototype.copy.call(phys, src);
  return phys;
}
